import type {Schemas} from 'api/dto.generated';
import {ebsApi} from 'store/ebsApi';
import type {
  CreateUserPreference,
  UserPreference,
  UserPreferenceCategory,
} from './types';

const userPreferencesApi = ebsApi.injectEndpoints({
  endpoints: build => ({
    getUserPreferences: build.query<
      UserPreference[],
      UserPreferenceCategory | undefined,
      Schemas.UserPreferenceDto[]
    >({
      query: category => ({
        url: `user-preferences`,
        params: {
          category,
        },
      }),
      // Dont need to transformResponse
      providesTags: (result, _, category) =>
        result
          ? [
              ...result.map(
                ({id}) =>
                  ({
                    type: 'UserPreference',
                    id,
                  }) as const,
              ),
              ...(category
                ? [{type: 'UserPreference', id: category} as const]
                : []),
            ]
          : category
            ? [{type: 'UserPreference', id: category}]
            : [],
    }),

    createUserPreference: build.mutation<
      UserPreference,
      CreateUserPreference,
      Schemas.CreateUserPreferenceDto
    >({
      query: userPreference => ({
        url: `user-preferences`,
        method: 'POST',
        body: userPreference,
      }),
      // Dont need to transformResponse
      invalidatesTags: userPreference =>
        userPreference && userPreference.id
          ? userPreference.category
            ? [
                {type: 'UserPreference', id: userPreference.id},
                {type: 'UserPreference', id: userPreference.category},
              ]
            : [{type: 'UserPreference', id: userPreference.id}]
          : [],
    }),

    updateUserPreference: build.mutation<
      UserPreference,
      UserPreference,
      Schemas.UserPreferenceDto
    >({
      query: userPreference => ({
        url: `user-preferences/${userPreference.id}`,
        method: 'PUT',
        body: userPreference,
      }),
      // Dont need to transformResponse
      invalidatesTags: (_, __, userPreference) =>
        userPreference.category
          ? [
              {type: 'UserPreference', id: userPreference.id},
              {type: 'UserPreference', id: userPreference.category},
            ]
          : [{type: 'UserPreference', id: userPreference.id}],
    }),

    deleteUserPreference: build.mutation<null, string>({
      query: id => ({
        url: `user-preferences/${id}`,
        method: 'DELETE',
      }),
      // Dont need to transformResponse
      invalidatesTags: (_, __, id) => [{type: 'UserPreference', id}],
    }),
  }),
});

export const {
  useGetUserPreferencesQuery,
  useCreateUserPreferenceMutation,
  useUpdateUserPreferenceMutation,
  useDeleteUserPreferenceMutation,
} = userPreferencesApi;
